import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardV2 } from "../../../components/CardV2/CardV2";
import ListContainer from "../../../components/ListContainer/ListContainer";
import ListElement from "../../../components/ListElement/ListElement";
import ElementActions from "../../../components/ElementActions/ElementActions";
import PrimaryButton from "../../../components/PrimaryButton/PrimaryButton";
import TextArea from "../../../components/TextArea/TextArea";
import IconButton from "../../../components/IconButton/IconButton";
import { Icon } from "../../../components/Icon/Icon";
import deleteIcon from "../../../assets/images/bin.svg";
import { isValidText } from "../../../shared/validation";

type ConditionItem = { id: string; text: string };

interface TermsConditionsSectionProps {
  contractTemplate: any;
  updateContractTemplate: (value: any) => void;
}

export default function TermsConditionsSection({ contractTemplate, updateContractTemplate }: TermsConditionsSectionProps) {
  const { t } = useTranslation();
  const currentConditions: string[] = contractTemplate?.conditions || [];
  const [newItem, setNewItem] = useState('');

  const elements: ConditionItem[] = currentConditions.map((text, i) => ({
    id: `term-condition-${i}`,
    text,
  }));

  const handleMovedItem = (reordered: ConditionItem[]) => {
    updateContractTemplate({ conditions: reordered.map((item) => item.text) });
  };

  const handleDeleteItem = (index: number) => {
    const updated = currentConditions.filter((_: string, i: number) => i !== index);
    updateContractTemplate({ conditions: updated });
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      updateContractTemplate({ conditions: [...currentConditions, newItem.trim()] });
      setNewItem('');
    }
  };

  return (
    <CardV2>
      <div className="p-4">
        <div className="text-xs text-muted-foreground mb-4">
          {String(t('commission_contract_template.contract_conditions_hint') ?? 'These are the detailed conditions that will appear in the final contract when brides sign it.')}
        </div>
        <ListContainer
          elements={elements}
          enableDragAndDrop
          onMovedItem={handleMovedItem}
          toElementFn={(item: ConditionItem, index: number) => (
            <ListElement>
              <ElementActions
                actions={
                  <IconButton onClick={() => handleDeleteItem(index)}>
                    <Icon url={deleteIcon} glyph cssStyles="w-5" />
                  </IconButton>
                }
              >
                <div className="flex-1 min-w-0">{item.text}</div>
              </ElementActions>
            </ListElement>
          )}
        />
        <TextArea
          label={String(t('commission_contract_template.contract_conditions.placeholder') ?? 'Add a contract condition...')}
          validationFn={(value) => !!value && isValidText(value, 5)}
          defaultValue={newItem}
          onChange={(e) => setNewItem(e.target.value)}
        />
        <div className="container flex flex-wrap space-x-5 mt-2">
          <PrimaryButton
            text={String(t('commission_contract_template.contract_conditions.add_button') ?? 'Add condition')}
            fn={handleAddItem}
          />
        </div>
      </div>
    </CardV2>
  );
}
